/**
 * useFocusRestore — restore focus to whichever element had it before
 * the modal mounted.
 * Call this from any overlay/dialog: when it unmounts, the previously
 * focused element regains focus.
 *
 * Why: WCAG 2.4.3 Focus Order — after a modal closes the user should
 * land back on the trigger that opened it, not at the top of the page.
 * Without this, keyboard users have to tab through the whole shell
 * again to get back to where they were.
 *
 * Restores synchronously, and that is load-bearing. It used to defer to
 * requestAnimationFrame, which is fine on its own but not once
 * useFocusTrap moves focus on mount: StrictMode mounts every effect
 * twice in development, so the first cleanup's deferred restore landed
 * after the second mount had already focused the dialog, and the second
 * mount then captured the dialog itself as the element to go back to. The
 * visible result was a sheet that never took focus and gave it to <body>
 * on close. Cleanups run in declaration order, so a consumer must call
 * this before useFocusTrap.
 *
 * Caveat: the restore is best-effort. If the trigger has been removed
 * from the DOM — which is what happens when a search row is clicked with
 * the mouse rather than picked with Enter — the restore is skipped and
 * focus stays where the browser put it.
 */
import { useEffect } from "react";

export function useFocusRestore(): void {
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    return () => {
      if (!previouslyFocused || typeof previouslyFocused.focus !== "function") return;
      if (!previouslyFocused.isConnected) return;
      previouslyFocused.focus({ preventScroll: true });
    };
  }, []);
}
